import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { X, Copy, Download, FileText, PenLine, SearchCheck, Stethoscope, Volume2, Square } from "lucide-react";
import { API } from "../../context/AuthContext";

const DOC_TYPES = [
  { key: "facts", label: "Statement of Facts", icon: FileText, file: "chronological-statement.txt", loading: "drafting your chronological statement of facts" },
  { key: "declaration", label: "Declaration", icon: PenLine, file: "declaration-draft.txt", loading: "drafting your court-ready declaration" },
  { key: "issues", label: "Issue Spotter", icon: SearchCheck, file: "legal-issues.txt", loading: "scanning the timeline for potential legal issues" },
  { key: "medical", label: "Medical Summary", icon: Stethoscope, file: "medical-treatment-summary.txt", loading: "compiling your detailed medical treatment summary" },
];

export default function SummaryModal({ caseId, onClose }) {
  const [docType, setDocType] = useState("facts");
  const [cache, setCache] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [speaking, setSpeaking] = useState(false);
  const [audio, setAudio] = useState(null);

  const toggleSpeak = async () => {
    if (speaking && audio) { audio.pause(); setSpeaking(false); return; }
    setSpeaking(true);
    try {
      const res = await axios.post(`${API}/tts`, { text: text.slice(0, 4000) }, { responseType: "blob" });
      const a = new Audio(URL.createObjectURL(res.data));
      a.onended = () => setSpeaking(false);
      setAudio(a);
      a.play();
    } catch {
      toast.error("Voice playback failed.");
      setSpeaking(false);
    }
  };
  const active = DOC_TYPES.find((d) => d.key === docType);
  const text = cache[docType] || "";

  useEffect(() => {
    if (cache[docType]) return;
    setLoading(true);
    setError("");
    axios.post(`${API}/summary`, { case_id: caseId, doc_type: docType })
      .then((res) => setCache((c) => ({ ...c, [docType]: res.data.text })))
      .catch((err) => setError(err.response?.data?.detail || "Professor Flux couldn't draft this document. Try again."))
      .finally(() => setLoading(false));
  }, [caseId, docType]); // eslint-disable-line react-hooks/exhaustive-deps

  const copy = () => {
    navigator.clipboard.writeText(text);
    toast.success(`${active.label} copied — paste it into your document!`);
  };

  const download = () => {
    const blob = new Blob([text], { type: "text/plain" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = active.file;
    a.click();
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4" onClick={onClose} data-testid="summary-modal">
      <div onClick={(e) => e.stopPropagation()} className="bg-[#131f3a] border border-white/10 rounded-2xl p-6 w-full max-w-2xl max-h-[85vh] flex flex-col gap-4 shadow-2xl">
        <div className="flex justify-between items-center">
          <h3 className="text-sm font-bold text-white uppercase tracking-wide font-display">Legal Writing Studio</h3>
          <button onClick={onClose} data-testid="summary-close-btn" className="text-gray-400 hover:text-white"><X className="w-4 h-4" /></button>
        </div>
        <div className="bg-gray-950 p-1 rounded-xl border border-white/5 flex gap-1">
          {DOC_TYPES.map((d) => {
            const Icon = d.icon;
            return (
              <button
                key={d.key}
                onClick={() => setDocType(d.key)}
                data-testid={`doc-type-${d.key}`}
                className={`flex-1 flex items-center justify-center gap-1.5 px-2 py-2 rounded-lg text-[10px] sm:text-[11px] font-semibold transition-colors duration-200 ${docType === d.key ? "bg-[#f59e0b]/15 text-[#f59e0b] border border-[#f59e0b]/30" : "text-gray-400 hover:text-white border border-transparent"}`}
              >
                <Icon className="w-3.5 h-3.5 shrink-0" />
                <span className="truncate">{d.label}</span>
              </button>
            );
          })}
        </div>
        {loading ? (
          <div className="text-xs text-[#f59e0b] font-mono animate-blink py-16 text-center">Professor Flux is {active.loading}...</div>
        ) : error ? (
          <div className="text-xs text-rose-400 py-12 text-center" data-testid="summary-error">{error}</div>
        ) : (
          <>
            <pre className="flex-1 overflow-y-auto bg-gray-950 border border-white/5 rounded-xl p-4 text-xs text-gray-200 whitespace-pre-wrap font-mono leading-relaxed" data-testid="summary-text">{text}</pre>
            {docType === "issues" && (
              <p className="text-[10px] text-gray-500 leading-relaxed">AI issue-spotting aid — not legal advice. Consult a licensed attorney.</p>
            )}
            {docType === "medical" && (
              <p className="text-[10px] text-gray-500 leading-relaxed">Generated from ingested records — verify against original medical records. Not medical advice.</p>
            )}
            <div className="flex gap-2">
              <button onClick={copy} data-testid="summary-copy-btn" className="flex-1 flex items-center justify-center gap-1.5 bg-[#f59e0b] hover:bg-[#fbbf24] text-gray-950 font-bold text-xs py-2.5 rounded-lg transition-colors duration-200">
                <Copy className="w-3.5 h-3.5" /> Copy to Clipboard
              </button>
              <button onClick={toggleSpeak} data-testid="summary-listen-btn" title="Read aloud" className="flex items-center justify-center gap-1.5 border border-white/10 text-gray-300 hover:text-white font-bold text-xs px-4 py-2.5 rounded-lg transition-colors duration-200">
                {speaking ? <Square className="w-3.5 h-3.5 text-rose-400" /> : <Volume2 className="w-3.5 h-3.5" />} {speaking ? "Stop" : "Listen"}
              </button>
              <button onClick={download} data-testid="summary-download-btn" className="flex items-center justify-center gap-1.5 border border-white/10 text-gray-300 hover:text-white font-bold text-xs px-4 py-2.5 rounded-lg transition-colors duration-200">
                <Download className="w-3.5 h-3.5" /> .txt
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
